import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useCdiAge } from '@/hooks/useCdiAge';

const SiteLoadingContext = createContext(null);

function SiteLoadingScreen({ cdiAge }) {
  const reduceMotion = useReducedMotion();
  const fadeDuration = reduceMotion ? 0.15 : 0.5;

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden bg-cdi-blue px-6 text-white"
      role="status"
      aria-live="polite"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: fadeDuration, ease: 'easeInOut' } }}
      transition={{ duration: reduceMotion ? 0.15 : 0.35, ease: 'easeOut' }}
    >
      <div className="absolute left-0 top-0 h-2 w-24 bg-cdi-yellow" aria-hidden="true" />
      <div className="absolute bottom-0 right-0 h-20 w-20 bg-cdi-yellow" aria-hidden="true" />
      <motion.div
        className="relative flex flex-col items-center"
        exit={{ opacity: 0, scale: reduceMotion ? 1 : 1.06, transition: { duration: fadeDuration, ease: 'easeInOut' } }}
      >
        <div className="relative flex h-48 w-48 items-center justify-center md:h-60 md:w-60" aria-hidden="true">
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-cdi-yellow/30 border-t-cdi-yellow"
            animate={reduceMotion ? undefined : { rotate: 360 }}
            transition={reduceMotion ? undefined : { duration: 1.5, ease: 'linear', repeat: Infinity }}
          />
          <motion.div
            className="relative flex flex-col items-center leading-none"
            animate={reduceMotion ? undefined : { scale: [0.94, 1, 0.94], opacity: [0.7, 1, 0.7] }}
            transition={reduceMotion ? undefined : { duration: 1.5, ease: 'easeInOut', repeat: Infinity }}
          >
            <span className="text-[8rem] font-black tracking-[-0.1em] text-white md:text-[10rem]">{cdiAge}</span>
            <span className="-mt-3 text-xs font-black uppercase tracking-[0.48em] text-cdi-yellow md:text-sm">Anos</span>
          </motion.div>
        </div>
        <p className="mt-8 text-3xl font-light tracking-tight md:text-4xl">CDI<span className="font-black text-cdi-yellow">PREMIUM</span></p>
        <div className="mt-5 h-1 w-36 overflow-hidden bg-white/20" aria-hidden="true">
          <motion.div
            className="h-full w-1/2 bg-cdi-yellow"
            animate={reduceMotion ? undefined : { x: ['-100%', '200%'] }}
            transition={reduceMotion ? undefined : { duration: 1.1, ease: 'easeInOut', repeat: Infinity }}
          />
        </div>
        <span className="mt-4 text-[10px] font-black uppercase tracking-[0.22em] text-white/60">Carregando</span>
      </motion.div>
    </motion.div>
  );
}

export function SiteLoadingProvider({ children }) {
  const cdiAge = useCdiAge();
  const [documentReady, setDocumentReady] = useState(() => document.readyState === 'complete');
  const [pendingTasks, setPendingTasks] = useState(() => new Set());

  useEffect(() => {
    const markDocumentReady = () => setDocumentReady(true);
    if (document.readyState === 'complete') {
      markDocumentReady();
      return undefined;
    }
    window.addEventListener('load', markDocumentReady, { once: true });
    return () => window.removeEventListener('load', markDocumentReady);
  }, []);

  const startTask = useCallback((task) => {
    setPendingTasks(current => {
      if (current.has(task)) return current;
      return new Set(current).add(task);
    });
  }, []);

  const finishTask = useCallback((task) => {
    setPendingTasks(current => {
      if (!current.has(task)) return current;
      const next = new Set(current);
      next.delete(task);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ startTask, finishTask }), [finishTask, startTask]);
  const loading = !documentReady || pendingTasks.size > 0;

  return (
    <SiteLoadingContext.Provider value={value}>
      {children}
      <AnimatePresence>
        {loading && <SiteLoadingScreen key="site-loading" cdiAge={cdiAge} />}
      </AnimatePresence>
    </SiteLoadingContext.Provider>
  );
}

export function useSiteLoadingTask(task) {
  const context = useContext(SiteLoadingContext);
  if (!context) throw new Error('useSiteLoadingTask deve ser usado dentro de SiteLoadingProvider.');

  const { startTask, finishTask } = context;
  useLayoutEffect(() => {
    startTask(task);
    return () => finishTask(task);
  }, [finishTask, startTask, task]);

  return useCallback(() => finishTask(task), [finishTask, task]);
}
